import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Factory, Package, Users, UserCircle, TruckIcon, BarChart3, LogOut, Menu, X, Settings, FileSearch, ClipboardList, Calculator, Upload, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) { console.error("Logout error:", err); }
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const isAdmin = user?.role === 'admin';

  const navigation = [
    { name: 'Dashboard', path: '/', icon: BarChart3, show: true },
    { name: 'MASTERS', header: true, show: isAdmin },
    { name: 'Item Master', path: '/masters/items', icon: Package, show: isAdmin },
    { name: 'Vendor Master', path: '/masters/vendors', icon: Users, show: isAdmin },
    { name: 'Customer Master', path: '/masters/customers', icon: UserCircle, show: isAdmin },
    { name: 'TRANSACTIONS', header: true, show: true },
    { name: 'RM Receipt', path: '/transactions/rm-receipt', icon: TruckIcon, show: true },
    { name: 'Cutting Production', path: '/transactions/cutting', icon: Factory, show: true },
    { name: 'Stage Issue', path: '/transactions/stage-issue', icon: TruckIcon, show: true },
    { name: 'Challans', path: '/transactions/challans', icon: FileSearch, show: true },
    { name: 'Stage Receipt', path: '/transactions/stage-receipt', icon: TruckIcon, show: true },
    { name: 'Part Conversion', path: '/transactions/conversion', icon: Package, show: true },
    { name: 'Dispatch', path: '/transactions/dispatch', icon: TruckIcon, show: true },
    { name: 'Rejection', path: '/transactions/rejection', icon: ClipboardList, show: true },
    { name: 'Sale Return', path: '/transactions/sale-return', icon: Package, show: true },
    { name: 'INVENTORY', header: true, show: true },
    { name: 'Opening Stock', path: '/inventory/opening-stock', icon: Package, show: true },
    { name: 'RM Stock', path: '/inventory/rm-stock', icon: Package, show: isAdmin },
    { name: 'Stage Stock', path: '/inventory/stage-stock', icon: Package, show: false },
    { name: 'Stock Summary', path: '/inventory/detailed-stock', icon: Package, show: isAdmin },
    { name: 'Pending/In-Process', path: '/inventory/pending', icon: Package, show: false },
    { name: 'Vendor WIP', path: '/inventory/vendor-wip', icon: Users, show: isAdmin },
    { name: 'RM Orders', path: '/inventory/rm-orders', icon: Package, show: true },
    { name: 'SCHEDULE', header: true, show: isAdmin },
    { name: 'Upload Schedule PDF', path: '/schedule/upload', icon: Upload, show: isAdmin },
    { name: 'Manage Schedule', path: '/schedule/manage', icon: Package, show: isAdmin },
    { name: 'Dispatch Plan', path: '/schedule/dispatch-plan', icon: Package, show: isAdmin },
    { name: 'Schedule vs Dispatch', path: '/schedule/vs-dispatch', icon: BarChart3, show: isAdmin },
    { name: 'REPORTS', header: true, show: isAdmin },
    { name: 'Part Traceability', path: '/reports/part-traceability', icon: FileSearch, show: isAdmin },
    { name: 'RM Consumption', path: '/reports/rm-consumption', icon: ClipboardList, show: isAdmin },
    { name: 'RM Requirement', path: '/reports/rm-requirement', icon: Calculator, show: isAdmin },
    { name: 'Stage Ageing', path: '/reports/stage-ageing', icon: Clock, show: isAdmin },
    { name: 'Monthly Report', path: '/reports/monthly-report', icon: BarChart3, show: isAdmin },
    { name: 'Weekly Target', path: '/reports/weekly-target', icon: BarChart3, show: isAdmin },
    { name: 'ADMIN', header: true, show: isAdmin },
    { name: 'Negative Stock Audit', path: '/admin/negative-stock-audit', icon: ClipboardList, show: isAdmin },
    { name: 'Data Management', path: '/admin/data-management', icon: Settings, show: isAdmin },
    { name: 'User Management', path: '/users', icon: Settings, show: isAdmin },
  ];

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-border transform transition-transform duration-200 ease-out lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border bg-gray-50/50">
            <img src="/mass-metalform-logo.png" alt="Mass MetalForm" className="h-12 w-auto mb-2" />
            <p className="text-xs text-text-secondary mt-1 tracking-wide uppercase">Production ERP</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-1">
              {navigation.filter(item => item.show).map((item, idx) => (
                item.header ? (
                  <li key={idx} className="pt-4 pb-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-text-muted px-3">
                      {item.name}
                    </h3>
                  </li>
                ) : (
                  <li key={idx}>
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.path}`}
                      className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-200 rounded-sm ${
                        isActive(item.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-text-secondary hover:bg-accent/10 hover:text-accent'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="tracking-wide">{item.name}</span>
                    </Link>
                  </li>
                )
              ))}
            </ul>
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-border bg-gray-50/50">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary truncate">{user?.full_name}</p>
                <p className="text-xs text-text-muted uppercase tracking-wider">{user?.role}</p>
              </div>
              <button
                data-testid="logout-button"
                onClick={handleLogout}
                className="p-2 hover:bg-accent/10 hover:text-accent transition-all rounded-sm"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-border">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-accent/10 hover:text-accent transition-all rounded-sm"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img src="/mass-metalform-logo.png" alt="Mass MetalForm" className="h-8 w-auto" />
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}